import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, ShieldCheck, XCircle, CheckCircle, Fire, ArrowsVertical, Package, Lock } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";

const TYPE_INFO = {
  hot_work: { label: "Trabajos en caliente", icon: Fire, checklist: ["Área despejada de material combustible", "Extintor disponible < 5m", "Vigía de fuego asignado", "Permisos ambientales OK"] },
  height: { label: "Trabajos en altura", icon: ArrowsVertical, checklist: ["Arnés inspeccionado y con línea de vida", "Punto de anclaje 22kN certificado", "Delimitación abajo", "Botiquín en sitio"] },
  confined_space: { label: "Espacio confinado", icon: Package, checklist: ["Atmósfera verificada (O2, CO, CH4, H2S)", "Vigía externo asignado", "Rescate en < 5 min", "Ventilación forzada activa"] },
  loto: { label: "Bloqueo/Etiquetado (LOTO)", icon: Lock, checklist: ["Fuente de energía identificada", "Bloqueos personales colocados", "Prueba de ausencia de energía", "Etiquetas visibles y firmadas"] },
  lifting: { label: "Izaje crítico", icon: Package, checklist: ["Plan de izaje aprobado", "Grúa inspeccionada", "Área acordonada", "Riggers certificados"] },
  excavation: { label: "Excavación", icon: Package, checklist: ["Mapeo de servicios enterrados", "Talud/entibado", "Escalera de acceso", "Vigilancia continua"] },
};

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

const emptyForm = () => ({ type: "hot_work", area: "", description: "", start_date: "", end_date: "", requested_by: "", checklist: {}, status: "pending" });

export default function PermitsModule() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const canManage = ["admin", "manager"].includes(user?.role);
  const canWrite = ["admin", "manager", "supervisor"].includes(user?.role);

  const load = useCallback(() => { api.get("/hse/permits").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const currentChecklist = TYPE_INFO[form.type].checklist;
      const checklist = {};
      currentChecklist.forEach((k) => { checklist[k] = form.checklist[k] || false; });
      await api.post("/hse/permits", { ...form, checklist, requested_by: form.requested_by || user?.name || "" });
      toast.success("Permiso solicitado");
      setOpen(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const approve = async (id) => {
    try { await api.post(`/hse/permits/${id}/approve`); toast.success("Aprobado"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const close = async (id) => {
    try { await api.post(`/hse/permits/${id}/close`); toast.success("Cerrado"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar permiso?")) return;
    try { await api.delete(`/hse/permits/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{items.length} permisos · {items.filter((x) => x.status === "pending").length} pendientes</p>
        {canWrite && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(emptyForm()); }}>
            <DialogTrigger asChild><Button data-testid="permit-new-button" className="bg-blue-900 hover:bg-blue-800 gap-2"><Plus size={14} weight="bold" /> Solicitar permiso</Button></DialogTrigger>
            <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Nuevo Permiso de Trabajo (PTS)</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="overline-label">Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, checklist: {} })}>
                    <SelectTrigger data-testid="permit-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Object.entries(TYPE_INFO).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="overline-label">Área</Label>
                  <Input data-testid="permit-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></div>
                <div><Label className="overline-label">Descripción</Label>
                  <Textarea data-testid="permit-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="overline-label">Inicio</Label>
                    <DateField testid="permit-start" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} /></div>
                  <div><Label className="overline-label">Fin</Label>
                    <DateField testid="permit-end" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} /></div>
                </div>
                <div>
                  <p className="overline-label mb-1">Checklist de verificación</p>
                  <div className="space-y-1 border border-slate-200 rounded p-2">
                    {TYPE_INFO[form.type].checklist.map((item, idx) => (
                      <label key={idx} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.checklist[item] || false} onChange={(e) => setForm({ ...form, checklist: { ...form.checklist, [item]: e.target.checked } })} data-testid={`permit-check-${idx}`} />
                        {item}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <Button data-testid="permit-save" onClick={save} disabled={!form.area || !form.description || !form.start_date} className="bg-blue-900 hover:bg-blue-800 w-full">Solicitar</Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 && <p className="text-sm text-slate-400 text-center py-8 col-span-2 bg-white border border-slate-200 rounded-lg">Sin permisos</p>}
        {items.map((it) => {
          const info = TYPE_INFO[it.type] || TYPE_INFO.hot_work;
          const Icon = info.icon;
          const total = Object.keys(it.checklist || {}).length;
          const checked = Object.values(it.checklist || {}).filter(Boolean).length;
          return (
            <div key={it.id} data-testid={`permit-card-${it.id}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                <Icon size={18} weight="duotone" className="text-orange-700" />
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-bold text-slate-900 text-sm">{info.label}</p>
                  <p className="text-xs text-slate-500">{it.area}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${STATUS_STYLE[it.status]}`}>{it.status}</span>
              </div>
              <div className="p-3 text-xs space-y-1">
                <p className="text-slate-700 line-clamp-2">{it.description}</p>
                <p className="text-slate-500">{it.start_date} → {it.end_date}</p>
                <p className="text-slate-500">Solicitado por: <b>{it.requested_by}</b></p>
                <p className="text-slate-500">Checklist: <b className={checked === total ? "text-emerald-700" : "text-amber-700"}>{checked}/{total}</b></p>
                {it.approved_by && <p className="text-emerald-700 text-[11px]">✓ Aprobado por {it.approved_by}</p>}
              </div>
              <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-end gap-1">
                {canManage && it.status === "pending" && checked === total && (
                  <Button size="sm" data-testid={`permit-approve-${it.id}`} onClick={() => approve(it.id)} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 gap-1"><CheckCircle size={12} weight="bold" /> Aprobar</Button>
                )}
                {canWrite && it.status === "approved" && (
                  <Button size="sm" variant="outline" data-testid={`permit-close-${it.id}`} onClick={() => close(it.id)} className="h-7 text-xs gap-1"><XCircle size={12} weight="bold" /> Cerrar</Button>
                )}
                {canManage && <button data-testid={`permit-delete-${it.id}`} onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-600 ml-1"><Trash size={14} /></button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
